/**
 * Clothing Set Manager Component
 * Displays list of clothing sets and allows creating, editing, and deleting them
 */

import { AcUnit, Add, Cloud, Delete, Edit, WbSunny } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ClothingSet, ClothingSetInput } from '@shared/types';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  createClothingSet,
  deleteClothingSet,
  getClothingSets,
  updateClothingSet,
} from '../../services/clothingSetsService';
import { LoadingAnimation } from '../common/LoadingAnimation';
import { ClothingSetEditor } from './ClothingSetEditor';

const getClothingImage = (item: string): string => {
  const map: Record<string, string> = {
    't-shirt-short': '/icons/cloths/tshirt short.png',
    't-shirt-long': '/icons/cloths/tshirt long.png',
    sweater: '/icons/cloths/sweater.png',
    shorts: '/icons/cloths/shorts.png',
    'long-tights': '/icons/cloths/thights.png',
    'running-cap': '/icons/cloths/cap.png',
    sweatband: '/icons/cloths/sweatband.png',
    gloves: '/icons/cloths/gloves.png',
    buff: '/icons/cloths/buff.png',
  };
  return map[item] || '';
};

const getTempIcon = (min: number, max: number) => {
  if (max < 10) return <AcUnit />;
  if (min > 20) return <WbSunny />;
  return <Cloud />;
};

export const ClothingSetManager: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sets, setSets] = useState<ClothingSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSet, setEditingSet] = useState<ClothingSet | undefined>();

  const loadSets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClothingSets();
      // Normalize clothing sets to ensure arrays for top/bottom (handles legacy data)
      const normalizedSets = data.map(set => ({
        ...set,
        clothing: {
          ...set.clothing,
          top: Array.isArray(set.clothing.top) ? set.clothing.top : [set.clothing.top],
          bottom: Array.isArray(set.clothing.bottom) ? set.clothing.bottom : [set.clothing.bottom],
          accessories: Array.isArray(set.clothing.accessories) ? set.clothing.accessories : [],
        },
      }));
      setSets(normalizedSets);
    } catch (err: any) {
      setError(err.message || 'Failed to load clothing sets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSets();
  }, []);

  // Handle edit query parameter
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && sets.length > 0) {
      const setToEdit = sets.find(s => s._id === editId);
      if (setToEdit) {
        handleEdit(setToEdit);
        // Clear the query parameter
        searchParams.delete('edit');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, sets, setSearchParams]);

  const handleCreate = () => {
    setEditingSet(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (set: ClothingSet) => {
    setEditingSet(set);
    setEditorOpen(true);
  };

  const handleSave = async (input: ClothingSetInput) => {
    try {
      if (editingSet?._id) {
        await updateClothingSet(editingSet._id, input);
      } else {
        await createClothingSet(input);
      }
      setEditorOpen(false);
      setEditingSet(undefined);
      await loadSets();
    } catch (err: any) {
      alert(err.message || 'Failed to save clothing set');
    }
  };

  const handleDelete = async (set: ClothingSet) => {
    if (!set._id) return;

    if (!confirm(`Delete "${set.name}"?`)) return;

    try {
      await deleteClothingSet(set._id);
      await loadSets();
    } catch (err: any) {
      alert(err.message || 'Failed to delete clothing set');
    }
  };

  const handleCancel = () => {
    setEditorOpen(false);
    setEditingSet(undefined);
  };

  if (loading) {
    return (
      <LoadingAnimation
        message="Loading clothing sets..."
        size={isMobile ? 'small' : 'medium'}
        minHeight={200}
        ariaLabel="Loading clothing sets"
      />
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5">Custom Clothing Sets</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/')} aria-label="Back to home page">
            Back to Home
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            aria-label="Create new clothing set"
          >
            Create Set
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {sets.length === 0 && !error && (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              No clothing sets yet. Create your first set to customize what to wear based on
              weather.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {sets.map(set => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={set._id}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography variant="h6" noWrap>
                      {set.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(set)}
                        aria-label={`Edit clothing set ${set.name}`}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(set)}
                        aria-label={`Delete clothing set ${set.name}`}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Temperature */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTempIcon(set.temperature.min, set.temperature.max)}
                    <Typography variant="body2">
                      {set.temperature.min}°C to {set.temperature.max}°C
                    </Typography>
                  </Box>

                  {/* Conditions */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {set.conditions.rain && <Chip label="Rain" size="small" color="primary" />}
                    {set.conditions.wind && <Chip label="Wind" size="small" color="primary" />}
                    {set.conditions.sun && <Chip label="Sun" size="small" color="secondary" />}
                    {!set.conditions.rain && !set.conditions.wind && !set.conditions.sun && (
                      <Chip label="Any weather" size="small" variant="outlined" />
                    )}
                  </Stack>

                  {/* Clothing Preview */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'center',
                      py: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    {set.clothing.top.map((item: string, idx: number) => (
                      <img
                        key={`top-${idx}`}
                        src={getClothingImage(item)}
                        alt="Top"
                        style={{ width: 40, height: 40 }}
                      />
                    ))}
                    {set.clothing.bottom.map((item: string, idx: number) => (
                      <img
                        key={`bottom-${idx}`}
                        src={getClothingImage(item)}
                        alt="Bottom"
                        style={{ width: 40, height: 40 }}
                      />
                    ))}
                    {set.clothing.accessories.map((item: string, idx: number) => (
                      <img
                        key={`acc-${idx}`}
                        src={getClothingImage(item)}
                        alt="Accessory"
                        style={{ width: 40, height: 40 }}
                      />
                    ))}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Editor Dialog */}
      <Dialog
        open={editorOpen}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <ClothingSetEditor existingSet={editingSet} onSave={handleSave} onCancel={handleCancel} />
      </Dialog>
    </Box>
  );
};
